import React, { useEffect, useState, useMemo, useRef } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CAStore from "../../Store/TemplateStore/template.store";
import {
  cleanData,
  storePlantData,
  storeUserEmail,
  storePlantRowData,
  storePlantDetails,
} from "../../Store/TemplateStore/template.action";
import Footer from "../../common/footer/Footer";
import {
  getPlants,
  deletePlants,
  getPlantsAvailableForBenchmark,
  getBenchmarkingData,
} from "../../network/cipAPI";
import Header from "../../components/Header";
import AgTable from "../../components/AgTable";
import DupontDialog from "../../components/DupontDialog";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import InputBox from "../../components/InputBox";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import { updatePlantId } from "../../utils/Utils";
import { saveItemSession } from "../../utils/session";
import ShareDialog from "../shareDialog";
import { PLANT_COL_DEF, advisorOptionsConfig } from "./plantsTable.config";
import {
  CompareIcon,
  DownloadIcon,
  NoPlantRoIcon,
  PlusIcon,
  RedoIcon,
} from "../../assets/icons";
import { API_URLS } from "../../network/appUrls";
import Benchmark from "../../components/Benchmark";
import WPDropdown from "../../packages/WPDropdown";
import WPEmptyStates from "../../packages/WPEmptyStates";
import { ADVISOR_TYPES } from "../../constants/global.constants";
import IconButton from "../../packages/IconButton";
import exportExcelReport from "../../components/Benchmark/DownloadBenchmarkReport/excelReport";
import { useToastNotification } from "../../packages/WPToastNotification";
import CircularLoader from "../../common/styles/components/circularLoader";
import { getIsValidAndValidTrains } from "./plantUtils";
import "./index.scss";

const basePath = API_URLS.basePath;

export const store = CAStore;

const PlantsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [loadHome, setloadHome] = useState(false);
  const [deletedRow, setDeletedRow] = useState(null);
  const [plantId, setPlantId] = useState(null);
  const [showDeletePopup, setShowDeletedDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [trainDialogOpen, setTrainDialogOpen] = useState(false);
  const [errors, setErrors] = useState();
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [noOfTrains, setNoOfTrains] = useState(1);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [isBenchmarkMode, setIsBenchmarkMode] = useState(false);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [advisorType, setAdvisorType] = useState(ADVISOR_TYPES.BOTH);
  const [benchmarkPlantTrains, setBenchmarkPlantTrains] = useState({});
  const gridRef = useRef(null);

  const { token, userEmail, requestStatus, plantRowData } = useSelector(
    (state) => state,
  );

  const plantColDef = useMemo(
    () => PLANT_COL_DEF(t, isBenchmarkMode, advisorType),
    [t, isBenchmarkMode, advisorType],
  );

  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const advisorOptions = useMemo(() => advisorOptionsConfig(t), [t]);

  const handleAdvisorChange = (option) => {
    setAdvisorType(option.value);
  };

  const openAddPlant = useMemo(
    () => (location.state?.openAddPlant ? "openAddPlant" : null),
    [location.state?.openAddPlant],
  );

  const getPlantDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getPlants();
      if (response && response.data) {
        const plantsWithTrain = response.data.map((plant) => {
          return {
            plant_id: plant.plant_id,
            sel_train: plant.sel_train,
            num_trains: plant.num_trains,
            is_owner: plant.is_owner,
          };
        });
        if (isBenchmarkMode) {
          await handleBenchmarkMode(response.data);
        } else {
          dispatch(storePlantRowData(response.data));
          dispatch(storePlantData(plantsWithTrain));
        }
        setIsLoading(false);
      } else {
        dispatch(storePlantRowData([]));
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("ValueStrings.somethingWentWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const deleteHandler = async (params) => {
    setDeletedRow(params);
    setShowDeletedDialog(true);
  };

  const addNewPlant = () => {
    setNoOfTrains(1);
    setTrainDialogOpen(true);
  };

  const onConfirmClicked = () => {
    updatePlantId(0);
    const path = `${basePath}addPlant`;
    navigate(path, { state: { noOfTrains } });
  };

  const editHandler = (params) => {
    updatePlantId(params.plant_id);
    const path = `${basePath}editPlant?plantId=${
      params.plant_id
    }&isEdit=${true}`;
    navigate(path, { state: { noOfTrains: params.num_trains } });
  };

  const shareHandler = (params) => {
    updatePlantId(params.plant_id);
    setPlantId(params.plant_id);
    setShareDialogOpen(true);
  };

  const onProceedClicked = async (value) => {
    setShowDeletedDialog(false);
    if (value === "CONFIRM") {
      try {
        updatePlantId(deletedRow.plant_id);
        const data = await deletePlants();

        if (data && data.status === 200) {
          const newPlantRowList = plantRowData.filter(
            (item) => item.plant_id !== deletedRow.plant_id,
          );
          dispatch(storePlantRowData(newPlantRowList));
          createNotification(
            SUCCESS_NOTIFICATION,
            t("notificationStrings.deleteSuccess"),
            "",
          );
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.deleteFail"),
            "",
          );
        }
      } catch (err) {}
    }
  };

  const navigateToAnalysisData = (data) => {
    updatePlantId(data.plant_id);
    const path = `${basePath}analyseData?plantId=${
      data.plant_id
    }&isEdit=${true}`;
    const plantsPageURL = window.location.pathname + window.location.search;
    saveItemSession("water_type", data.water_type);
    saveItemSession("plantName", data.name);
    saveItemSession("plantsPageURL", plantsPageURL);
    navigate(path);
  };

  const handleClickOnPlantRow = (event) => {
    const clickedElement = event.event.target;
    if (clickedElement.closest(".custom-sharing-button")) {
      return;
    }

    const { data } = event;
    if (data.view === 1) editHandler(data);
    else if (data.view === 2) {
      navigateToAnalysisData(data);
    } else if (data.view === 3) {
      updatePlantId(data.plant_id);
      dispatch(storePlantDetails({ num_trains: data.num_trains }));
      const path = `${basePath}home?plantId=${data.plant_id}`;
      navigate(path);
    }
  };

  const handleInputChange = (value) => {
    setNoOfTrains(value);
    let errorMessage = null;
    if (value === " " || value > 20 || value < 1) {
      errorMessage = `${t("roAdvisorProject.errorMesage")} `;
    }
    setErrors(errorMessage);
  };

  const onClose = (action) => {
    if (action === "CONFIRM") {
      onConfirmClicked();
    } else if (action === "CANCEL") {
      setTrainDialogOpen(false);
      setNoOfTrains(1);
      setErrors(null);
    }
  };

  const handleBenchmarkMode = async (existingTrainData = []) => {
    setIsLoading(true);
    try {
      const response = await getPlantsAvailableForBenchmark();
      if (response && response.data) {
        const benchmarkPlantData = response.data.plants;
        const benchmarkMap = new Map(
          benchmarkPlantData?.map((item) => [item.plant_id, item]),
        );

        const plantData =
          existingTrainData.length > 0 ? existingTrainData : plantRowData;

        const mergedData = plantData.map((plant) => ({
          ...plant,
          ...(benchmarkMap.get(plant.plant_id) ?? {}),
        }));

        setIsBenchmarkMode(true);
        setTimeout(() => {
          dispatch(storePlantRowData(mergedData));
        }, 0);
      } else {
        throw new Error("No plants available for benchmark");
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("ValueStrings.somethingWentWrong"),
        "",
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  };

  const handleRefreshPlants = async () => {
    setSelectedPlants([]);
    await getPlantDetails();
  };

  const getRowClass = (params) => {
    if (isBenchmarkMode) {
      const { isValid } = getIsValidAndValidTrains(params.data, advisorType);
      return isValid ? "" : "disabled-row";
    }
    return "";
  };

  const handlePlantSelection = (plant, isChecked) => {
    setSelectedPlants((prevSelectedPlants) => {
      if (isChecked) {
        const { validTrains } = getIsValidAndValidTrains(plant, advisorType);

        const { name, plant_id, overall_ca_status, overall_ra_status, trains } =
          plant;

        return [
          ...prevSelectedPlants,
          {
            originalTrains: trains,
            plant_name: name,
            plant_id,
            trains: validTrains,
            overall_ca_status,
            overall_ra_status,
          },
        ];
      } else {
        return prevSelectedPlants.filter((p) => p.plant_id !== plant.plant_id);
      }
    });
  };

  const filterSelectedPlants = () => {
    setSelectedPlants((prevSelectedPlants) => {
      return prevSelectedPlants
        .map((plant) => {
          const { validTrains, isValid } = getIsValidAndValidTrains(
            plant,
            advisorType,
          );
          return isValid ? { ...plant, trains: validTrains } : null;
        })
        .filter(Boolean);
    });
  };

  const selectedTrainsCount = useMemo(() => {
    return Object.values(benchmarkPlantTrains).reduce(
      (count, trainIds) => count + trainIds.length,
      0,
    );
  }, [benchmarkPlantTrains]);

  const handleDownloadBenchmarkReport = async () => {
    try {
      setIsLoading(true);

      const payload = {
        plant_train_filters: benchmarkPlantTrains,
        filter_type: advisorType,
      };

      const response = await getBenchmarkingData(payload);
      const { plants = [] } = response;

      const trainsData = plants.flatMap(
        ({ plant_id, plant_name, units, trains = [] }) =>
          trains.map(
            ({
              train_num,
              ca_predictions,
              fouling_data,
              ra_predictions,
              cleaning_efficiency,
              settings = {},
            }) => ({
              train_num,
              plant_id,
              plant_name,
              train_name: `Plant ${plant_id} - Train ${train_num}`,
              ca_predictions,
              fouling_data,
              ra_predictions,
              cleaning_efficiency,
              optimization_target: settings.optimization_target,
              units,
            }),
          ),
      );

      if (trainsData.length === 0) {
        showErrorNotification(t("ValueStrings.somethingWentWrong"));
        return;
      }

      await exportExcelReport(
        trainsData,
        `${t("benchMark.benchmarkReport")}`,
        plants,
        payload.filter_type,
      );
      showSuccessNotification(t("benchMark.benchmarkingReportDownloaded"));
    } catch (err) {
      showErrorNotification(
        err?.message || t("benchMark.benchmarkingReportDownloadFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updatePlantId();
    setloadHome(true);
    dispatch(cleanData());
  }, []);

  useEffect(() => {
    dispatch(storeUserEmail(userEmail));
  }, [userEmail]);

  useEffect(() => {
    if (errors) {
      setConfirmDisabled(true);
    } else {
      setConfirmDisabled(false);
    }
  }, [errors]);

  // Auto-open add plant dialog when
  // navigated with openAddPlant state, plantRowData is available and loading state is false
  useEffect(() => {
    if (openAddPlant && plantRowData && !isLoading) {
      addNewPlant();
    }
  }, [isLoading, openAddPlant, plantRowData]);

  useEffect(() => {
    if (
      token &&
      (requestStatus === -1 ||
        requestStatus === 2 ||
        requestStatus === "-1" ||
        requestStatus === "2")
    ) {
      getPlantDetails();
    }
  }, [token, requestStatus]);

  useEffect(() => {
    if (!isBenchmarkMode) {
      setSelectedPlants([]);
      if (gridRef.current?.api) {
        gridRef.current.api.redrawRows();
      }
    }
  }, [isBenchmarkMode]);

  useEffect(() => {
    if (isBenchmarkMode && gridRef.current?.api) {
      gridRef.current.api.redrawRows();
    }
  }, [selectedPlants, isBenchmarkMode, advisorType]);

  useEffect(() => {
    if (isBenchmarkMode && selectedPlants.length > 0) {
      setTimeout(() => {
        filterSelectedPlants();
      }, 0);
    }
  }, [advisorType]);

  useEffect(() => {
    if (
      (location.pathname === "/" || location.pathname.includes("/plant")) &&
      isBenchmarkMode
    ) {
      setIsBenchmarkMode(false);
    }
  }, [location.key]);

  return (
    <Provider store={store}>
      {loadHome && (
        <>
          <div className="container-main">
            <Header />
            <div
              className={
                "main-container " + (isBenchmarkMode ? "benchmark-mode" : "")
              }
            >
              <div className="content-container descriptionText plants-page-container">
                {(isBenchmarkMode || plantRowData.length > 0) && (
                  <div className="flex-between-center plants-header-container">
                    <div className="plants-page-heading">
                      <div className="plants-page-heading-text">
                        {!isBenchmarkMode ? (
                          t("roAdvisorProject.allPlants")
                        ) : (
                          <div>
                            <CompareIcon /> {t("benchMark.benchmarkPlants")}
                          </div>
                        )}
                      </div>
                      {isBenchmarkMode && (
                        <div className="plants-page-disclaimer">
                          {t("benchMark.disclaimer")}
                        </div>
                      )}
                    </div>

                    <div className="plant-page-actions">
                      <IconButton
                        tooltip={t("benchMark.refreshPlants")}
                        placement="bottom"
                        onClick={handleRefreshPlants}
                      >
                        <WPTertiaryButton
                          icon={<RedoIcon />}
                          className="refresh-plants"
                          disabled={isLoading}
                          preventDefault={false}
                        />
                      </IconButton>
                      {isBenchmarkMode && (
                        <WPDropdown
                          id="advisor-options"
                          value={advisorType}
                          options={advisorOptions}
                          onChange={handleAdvisorChange}
                        />
                      )}

                      {!isBenchmarkMode ? (
                        <>
                          <WPTertiaryButton
                            onClick={handleBenchmarkMode}
                            label={t("benchMark.benchmarkPlants")}
                            icon={<CompareIcon />}
                            iconPlacement="before"
                            disabled={isLoading}
                            variant="outlined"
                            type="secondary"
                            className="benchmark-button"
                          />

                          <WPTertiaryButton
                            label={t("roAdvisorProject.addPlant")}
                            onClick={addNewPlant}
                            icon={<PlusIcon />}
                            iconPlacement="before"
                            disabled={isLoading}
                            variant="contained"
                          />
                        </>
                      ) : (
                        <WPTertiaryButton
                          variant="contained"
                          label={t("benchMark.benchmarkReport")}
                          icon={<DownloadIcon />}
                          iconPlacement="before"
                          onClick={handleDownloadBenchmarkReport}
                          size="small"
                          disabled={selectedTrainsCount < 2 || isLoading}
                          className="benchmark-report"
                        />
                      )}
                    </div>
                  </div>
                )}
                {trainDialogOpen && (
                  <DupontDialog
                    open={trainDialogOpen}
                    title={t("roAdvisorProject.trainDialogTitle")}
                    cancelBtnText={t("ValueStrings.cancel")}
                    confirmBtnText={t("ValueStrings.enter")}
                    confirmDisabled={confirmDisabled}
                    onClose={onClose}
                    minWidth={575}
                  >
                    <div className="flex-between-center">
                      <label className="font-14 descriptionText">
                        {t("roAdvisorProject.trainDialogText")}
                      </label>
                      <div className="dropdown-width marginBtm10">
                        <InputBox
                          value={noOfTrains}
                          validator={TextFieldValidator.NUMBER_ONLY}
                          maxLength={2}
                          onChange={(value) => handleInputChange(value)}
                          errorMsg={errors}
                        />
                      </div>
                    </div>
                    {noOfTrains > 1 && noOfTrains <= 20 && (
                      <div className="noteText">
                        {t("roAdvisorProject.noteMessage").replace(
                          "<value>",
                          noOfTrains,
                        )}
                      </div>
                    )}
                  </DupontDialog>
                )}
                <div className="plants-content-container">
                  <div className="left-content">
                    <div className="plants-table-container">
                      {isLoading && (
                        <div className="circular-loader-overlay">
                          <CircularLoader />
                        </div>
                      )}

                      {plantRowData.length > 0 && (
                        <>
                          <AgTable
                            gridRef={gridRef}
                            columnDefs={plantColDef}
                            rowData={plantRowData}
                            customHeight="100%"
                            hideExport
                            name={`${t("excelSheetNames.plants")}`}
                            context={{
                              deleteHandler,
                              editHandler,
                              shareHandler,
                              userEmail,
                              onPlantSelection: handlePlantSelection,
                              selectedPlants,
                            }}
                            onRowClicked={
                              !isBenchmarkMode
                                ? handleClickOnPlantRow
                                : undefined
                            }
                            getRowClass={getRowClass}
                          />
                          {showDeletePopup && (
                            <DupontDialog
                              open={showDeletePopup}
                              type="primary"
                              title={t(
                                "notificationStrings.deleteConfirmationPlant",
                              )}
                              cancelBtnText={t("ValueStrings.no")}
                              confirmBtnText={t("ValueStrings.yes")}
                              onClose={onProceedClicked}
                            />
                          )}
                          {shareDialogOpen && (
                            <ShareDialog
                              setShareDialogOpen={setShareDialogOpen}
                              shareDialogOpen={shareDialogOpen}
                              plantId={plantId}
                            />
                          )}
                        </>
                      )}

                      {!isLoading && plantRowData.length === 0 && (
                        <div className="plants-empty-state">
                          <WPEmptyStates
                            className="plants-empty-state__message"
                            icon={NoPlantRoIcon}
                            titleMsg={t(
                              "roAdvisorProject.noPlantsCreatedTitle",
                            )}
                            description={t(
                              "roAdvisorProject.noPlantsCreatedDescription",
                            )}
                            buttonText={t("roAdvisorProject.addPlant")}
                            onButtonClick={addNewPlant}
                            buttonIcon={<PlusIcon />}
                            buttonIconPlacement="before"
                            buttonVariant="contained"
                            buttonSize="medium"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {isBenchmarkMode && (
                    <div className="right-content">
                      <Benchmark
                        setIsBenchmarkMode={setIsBenchmarkMode}
                        selectedPlants={selectedPlants}
                        benchmarkPlantTrains={benchmarkPlantTrains}
                        setBenchmarkPlantTrains={setBenchmarkPlantTrains}
                        selectedTrainsCount={selectedTrainsCount}
                        isLoading={isLoading}
                      />
                    </div>
                  )}
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </>
      )}
    </Provider>
  );
};

export default PlantsPage;
